// 5.24.0 FAQ — full-window overlay (same pattern as Renewals). docs/faq/faq_plan.md.
// Everyone: pick a company, ask; answers stream from POST /api/faq/ask (faqStream.js) with citation cards.
// Admin only (role === 'admin'; managers excluded): Companies, Documents, Facts, Conflicts, Question log.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { faq } from './api';
import { authFetch } from './authSession';
import { readNdjson, statusText } from './faqStream';
import { CitationList, ConflictBox, fmtDate } from './FaqCitations';

const BLUE = '#16a085';
const errText = (e, fallback) => e?.response?.data?.detail || e?.message || fallback;

function useTheme(darkMode) {
  return {
    darkMode,
    bg: darkMode ? '#1a1a1a' : '#f5f5f5',
    panelBg: darkMode ? '#242424' : '#fff',
    borderColor: darkMode ? '#333' : '#e5e5e5',
    textPrimary: darkMode ? '#f0f0f0' : '#1a1a1a',
    textSecondary: darkMode ? '#aaa' : '#666',
    inputBg: darkMode ? '#1a1a1a' : '#fff',
  };
}

const btnStyle = (t, primary) => ({
  padding: '6px 12px', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  background: primary ? BLUE : t.panelBg, color: primary ? '#fff' : t.textPrimary,
  border: primary ? 'none' : `1px solid ${t.borderColor}`,
});
const inputStyle = (t) => ({
  padding: '6px 8px', borderRadius: 5, border: `1px solid ${t.borderColor}`, background: t.inputBg,
  color: t.textPrimary, fontSize: 13,
});

// ---------------------------------------------------------------------------------------------------
// Source viewer: PDF at the cited page (blob URL + #page=N), or the page text for .txt/.md.

function SourceViewer({ cite, t, onClose }) {
  const [url, setUrl] = useState(null);
  const [text, setText] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    let made = null;
    (async () => {
      try {
        if (cite.mime === 'application/pdf') {
          const r = await faq.file(cite.document_id);
          made = window.URL.createObjectURL(r.data);
          if (alive) setUrl(`${made}#page=${cite.page || 1}`);
        } else {
          const r = await faq.page(cite.document_id, cite.page || 1);
          if (alive) setText(r.data.text);
        }
      } catch (e) { if (alive) setErr(errText(e, 'Could not open the document')); }
    })();
    return () => { alive = false; if (made) window.URL.revokeObjectURL(made); };
  }, [cite]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 950, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(1100px, 94vw)', height: '90vh', background: t.panelBg, color: t.textPrimary, borderRadius: 8, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '8px 14px', borderBottom: `1px solid ${t.borderColor}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 13, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cite.title || cite.filename} · {fmtDate(cite.doc_date)} · page {cite.page}
          </span>
          <button style={btnStyle(t)} onClick={onClose}>Close</button>
        </div>
        {cite.quote && (
          <div style={{ padding: '6px 14px', fontSize: 12, fontStyle: 'italic', borderBottom: `1px solid ${t.borderColor}`, background: t.darkMode ? '#2b2416' : '#fffbe6' }}>
            Quoted: “{cite.quote}”
          </div>
        )}
        {err && <div style={{ padding: 20, color: '#c0392b' }}>{err}</div>}
        {url && <iframe title="faq-source" src={url} style={{ flex: 1, border: 'none', width: '100%' }} />}
        {text !== null && <pre style={{ flex: 1, margin: 0, padding: 16, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 13 }}>{text}</pre>}
        {!err && !url && text === null && <div style={{ padding: 20, color: t.textSecondary }}>Loading…</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// Ask

function AskTab({ t, companies, companyId, setCompanyId, onOpen }) {
  const [question, setQuestion] = useState('');
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread, status]);

  const ask = async (q, cid) => {
    if (!q || !cid || busy) return;
    const company = companies.find((c) => c.id === cid);
    setBusy(true);
    setStatus('🧠 Thinking — this can take a minute or two…');
    let got = false;
    const push = (m) => setThread((prev) => [...prev, { question: q, companyId: cid, company: company?.name, ...m }]);
    try {
      const res = await authFetch('/api/faq/ask', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: cid, question: q }),
      });
      if (!res.ok) {
        let detail = `Request failed (HTTP ${res.status})`;
        try { detail = (await res.json()).detail || detail; } catch (_e) { /* not json */ }
        push({ error: detail });
        got = true;
      } else {
        await readNdjson(res, (ev) => {
          if (ev.event === 'status' || ev.event === 'retry') setStatus(statusText(ev));
          else if (ev.event === 'answer') { got = true; push({ answer: ev }); }
          else if (ev.event === 'error') { got = true; push({ error: ev.detail || 'Request failed' }); }
        });
      }
      if (!got) push({ error: 'The answer did not arrive — please try again.' });
    } catch (_e) {
      push({ error: 'Connection error — the FAQ service may be busy.' });
    }
    setBusy(false);
    setStatus(null);
  };

  const submit = () => {
    const q = question.trim();
    if (!q || !companyId) return;
    setQuestion('');
    ask(q, companyId);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, maxWidth: 1000, width: '100%', margin: '0 auto' }}>
      <div style={{ padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
        <label style={{ fontSize: 13, fontWeight: 600 }}>Company</label>
        <select data-testid="faq-company" value={companyId || ''} onChange={(e) => setCompanyId(Number(e.target.value) || null)}
          style={{ ...inputStyle(t), minWidth: 260 }}>
          <option value="">— pick a company —</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {companyId && (() => {
          const c = companies.find((x) => x.id === companyId);
          return c ? <span style={{ fontSize: 12, color: t.textSecondary }}>{c.documents} document(s) · {c.facts} fact(s)</span> : null;
        })()}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
        {thread.length === 0 && (
          <div style={{ color: t.textSecondary, fontSize: 13, padding: '30px 0', textAlign: 'center' }}>
            Pick a company and ask a question — claims, underwriting, billing, contacts…<br />
            Answers come only from that company's documents and the agency's facts, with the sources shown underneath.
          </div>
        )}
        {thread.map((m, i) => (
          <div key={i} style={{ margin: '12px 0' }}>
            <div style={{ fontSize: 12, color: t.textSecondary }}>{m.company}</div>
            <div style={{ background: BLUE, color: '#fff', borderRadius: 10, padding: '8px 12px', display: 'inline-block', maxWidth: '85%', fontSize: 13, whiteSpace: 'pre-wrap' }}>
              {m.question}
            </div>
            {m.error && (
              <div data-testid="faq-error" style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid #c0392b', color: '#c0392b', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1 }}>⚠ {m.error}</span>
                <button style={btnStyle(t)} disabled={busy} onClick={() => ask(m.question, m.companyId)}>Retry</button>
              </div>
            )}
            {m.answer && (
              <div style={{ marginTop: 8, background: t.panelBg, border: `1px solid ${t.borderColor}`, borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{m.answer.answer}</div>
                {!m.answer.answered && (
                  <div style={{ marginTop: 6, fontSize: 11, color: '#b45309' }}>
                    {m.answer.model_answered && !m.answer.verified
                      ? '⚠ Not verified against the material — the sources quoted could not be found. Check with an admin.'
                      : 'The material on file does not answer this. An admin can add a fact or document.'}
                  </div>
                )}
                {(m.answer.conflicts || []).map((c) => <ConflictBox key={c.conflict_id} conflict={c} darkMode={t.darkMode} onOpen={onOpen} />)}
                <CitationList citations={m.answer.citations} darkMode={t.darkMode} onOpen={onOpen} />
                <div style={{ marginTop: 6, fontSize: 10, color: t.textSecondary }}>
                  {m.answer.model} · {Math.round((m.answer.latency_ms || 0) / 100) / 10}s
                  {m.answer.docs_omitted ? ` · ${m.answer.docs_omitted} older document(s) left out (too long)` : ''}
                </div>
              </div>
            )}
          </div>
        ))}
        {status && <div data-testid="faq-status" style={{ fontSize: 12, color: t.textSecondary, margin: '8px 0' }}>{status}</div>}
        <div ref={endRef} />
      </div>
      <div style={{ padding: 12, borderTop: `1px solid ${t.borderColor}`, display: 'flex', gap: 8 }}>
        <textarea data-testid="faq-question" value={question} onChange={(e) => setQuestion(e.target.value)} rows={2}
          placeholder={companyId ? 'Ask about this company…' : 'Pick a company first'}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          style={{ ...inputStyle(t), flex: 1, resize: 'vertical', fontFamily: 'inherit' }} />
        <button data-testid="faq-ask" style={btnStyle(t, true)} disabled={busy || !companyId || !question.trim()} onClick={submit}>
          {busy ? 'Asking…' : 'Ask'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// Admin: companies

function CompaniesTab({ t, reload }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState('');
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => faq.companies(true).then((r) => setRows(r.data)).catch((e) => setMsg(errText(e, 'Load failed'))), []);
  useEffect(() => { load(); }, [load]);
  const after = () => { load(); reload(); };
  const create = async () => {
    try { await faq.createCompany(name); setName(''); setMsg(null); after(); } catch (e) { setMsg(errText(e, 'Create failed')); }
  };
  const rename = async (c) => {
    const n = window.prompt('New name', c.name);
    if (!n || n === c.name) return;
    try { await faq.patchCompany(c.id, { name: n }); after(); } catch (e) { setMsg(errText(e, 'Rename failed')); }
  };
  const toggle = async (c) => {
    try { await faq.patchCompany(c.id, { is_active: !c.is_active }); after(); } catch (e) { setMsg(errText(e, 'Update failed')); }
  };
  return (
    <div style={{ padding: 16, maxWidth: 800 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New company name" style={{ ...inputStyle(t), flex: 1 }}
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
        <button style={btnStyle(t, true)} disabled={!name.trim()} onClick={create}>+ Add company</button>
      </div>
      {msg && <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 8 }}>{msg}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ textAlign: 'left', color: t.textSecondary }}><th>Company</th><th>Docs</th><th>Facts</th><th>Open conflicts</th><th /></tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} style={{ borderTop: `1px solid ${t.borderColor}`, opacity: c.is_active ? 1 : 0.5 }}>
              <td style={{ padding: '6px 0' }}>{c.name}{c.is_active ? '' : ' (hidden)'}</td>
              <td>{c.documents}</td><td>{c.facts}</td><td>{c.open_conflicts || 0}</td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button style={btnStyle(t)} onClick={() => rename(c)}>Rename</button>{' '}
                <button style={btnStyle(t)} onClick={() => toggle(c)}>{c.is_active ? 'Hide' : 'Show'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// Admin: documents

const SOURCE_CHIP = {
  detected: { label: 'detected', bg: '#0e7c86' },
  upload_date: { label: 'upload date', bg: '#7f8c8d' },
  manual: { label: 'manual', bg: '#8e44ad' },
};
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function DocumentsTab({ t, companyId, reload, onOpen }) {
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(todayIso());
  const [dateTouched, setDateTouched] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => {
    if (!companyId) { setRows([]); return; }
    faq.documents(companyId).then((r) => setRows(r.data)).catch((e) => setMsg(errText(e, 'Load failed')));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  // Poll while anything is still extracting (OCR runs in the background).
  useEffect(() => {
    if (!rows.some((d) => d.status === 'pending' || d.status === 'extracting')) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [rows, load]);

  const pick = (f) => {
    // 5.27.2: no date detection — the date is today (the upload date) unless the admin changes it.
    setFile(f); setTitle(f ? f.name.replace(/\.[^.]+$/, '') : ''); setDate(todayIso()); setDateTouched(false); setMsg(null);
  };
  const upload = async () => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const r = await faq.upload(companyId, file, dateTouched ? date : null, title.trim());
      if (r.data.duplicate_of) setMsg(`Uploaded. Note: the same file is already on file (document #${r.data.duplicate_of}); both are kept.`);
      setFile(null); setTitle(''); setDateTouched(false); setDate(todayIso());
      if (fileRef.current) fileRef.current.value = '';
      load(); reload();
    } catch (e) { setMsg(errText(e, 'Upload failed')); }
    setBusy(false);
  };
  const edit = async (d, field) => {
    const v = window.prompt(field === 'title' ? 'Title' : 'Document date (YYYY-MM-DD)', field === 'title' ? d.title : d.doc_date);
    if (!v || v === (field === 'title' ? d.title : d.doc_date)) return;
    try { await faq.patchDocument(d.id, { [field]: v }); load(); } catch (e) { setMsg(errText(e, 'Update failed')); }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete “${d.title}”? It will no longer be used for answers (the file is kept).`)) return;
    try { await faq.deleteDocument(d.id); load(); reload(); } catch (e) { setMsg(errText(e, 'Delete failed')); }
  };
  const reex = async (d) => { try { await faq.reextract(d.id); load(); } catch (e) { setMsg(errText(e, 'Re-extract failed')); } };

  if (!companyId) return <div style={{ padding: 16, color: t.textSecondary }}>Pick a company above.</div>;
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <input ref={fileRef} type="file" accept=".pdf,.txt,.md,application/pdf,text/plain,text/markdown"
          onChange={(e) => pick(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={{ ...inputStyle(t), width: 260 }} />
        <label style={{ fontSize: 12 }}>Document date</label>
        <input type="date" value={date} max={todayIso()} onChange={(e) => { setDate(e.target.value); setDateTouched(true); }} style={inputStyle(t)} />
        <button style={btnStyle(t, true)} disabled={!file || busy} onClick={upload}>{busy ? 'Uploading…' : 'Upload'}</button>
      </div>
      {msg && <div style={{ fontSize: 12, color: '#b45309', marginBottom: 6 }}>{msg}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ textAlign: 'left', color: t.textSecondary }}><th>Title</th><th>Date</th><th>Pages</th><th>Status</th><th /></tr></thead>
        <tbody>
          {rows.map((d, i) => {
            const chip = SOURCE_CHIP[d.doc_date_source] || SOURCE_CHIP.upload_date;
            return (
              <tr key={d.id} style={{ borderTop: `1px solid ${t.borderColor}` }}>
                <td style={{ padding: '6px 0' }}>
                  <span style={{ cursor: d.status !== 'failed' ? 'pointer' : 'default', textDecoration: 'underline dotted' }}
                    onClick={() => onOpen({ kind: 'document', document_id: d.id, page: 1, title: d.title, filename: d.filename, doc_date: d.doc_date, mime: d.mime, quote: '' })}>
                    {d.title}
                  </span>
                  {i === 0 && <span style={{ marginLeft: 6, fontSize: 10, color: BLUE, fontWeight: 700 }}>NEWEST</span>}
                  <div style={{ fontSize: 11, color: t.textSecondary }}>{d.filename}</div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {fmtDate(d.doc_date)}{' '}
                  <span data-testid="faq-date-source" title={d.doc_date_evidence || (d.doc_date_source === 'manual' ? 'Set by an admin' : 'Upload date')}
                    style={{ background: chip.bg, color: '#fff', borderRadius: 8, padding: '1px 6px', fontSize: 10, cursor: 'help' }}>{chip.label}</span>
                </td>
                <td>{d.page_count ?? '—'}</td>
                <td title={d.extract_note || ''}>
                  {d.stuck ? 'stuck' : d.status}{d.extract_note && d.extract_note !== 'ok' ? ' ⓘ' : ''}
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button style={btnStyle(t)} onClick={() => edit(d, 'title')}>Title</button>{' '}
                  <button style={btnStyle(t)} onClick={() => edit(d, 'doc_date')}>Date</button>{' '}
                  {(d.status === 'failed' || d.status === 'partial' || d.stuck) && <><button style={btnStyle(t)} onClick={() => reex(d)}>Re-extract</button>{' '}</>}
                  <button style={{ ...btnStyle(t), color: '#c0392b' }} onClick={() => del(d)}>Delete</button>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={5} style={{ padding: 12, color: t.textSecondary }}>No documents yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// Admin: facts

function FactsTab({ t, companyId, reload }) {
  const [rows, setRows] = useState([]);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => {
    if (!companyId) { setRows([]); return; }
    faq.facts(companyId).then((r) => setRows(r.data)).catch((e) => setMsg(errText(e, 'Load failed')));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { try { await faq.addFact(companyId, text); setText(''); load(); reload(); } catch (e) { setMsg(errText(e, 'Add failed')); } };
  const save = async () => { try { await faq.editFact(editing.id, editing.text); setEditing(null); load(); } catch (e) { setMsg(errText(e, 'Save failed')); } };
  const del = async (f) => {
    if (!window.confirm('Delete this fact?')) return;
    try { await faq.deleteFact(f.id); load(); reload(); } catch (e) { setMsg(errText(e, 'Delete failed')); }
  };
  if (!companyId) return <div style={{ padding: 16, color: t.textSecondary }}>Pick a company above.</div>;
  return (
    <div style={{ padding: 16, maxWidth: 900 }}>
      <div style={{ fontSize: 12, color: t.textSecondary, marginBottom: 6 }}>Facts outrank documents. Keep each one short and specific.</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="New fact, e.g. “Claims phone: 800-555-0100 (24/7)”"
          style={{ ...inputStyle(t), flex: 1, fontFamily: 'inherit' }} />
        <button style={btnStyle(t, true)} disabled={!text.trim()} onClick={add}>+ Add fact</button>
      </div>
      {msg && <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 8 }}>{msg}</div>}
      {rows.map((f) => (
        <div key={f.id} style={{ borderTop: `1px solid ${t.borderColor}`, padding: '8px 0', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {editing?.id === f.id ? (
            <>
              <textarea value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} rows={3}
                style={{ ...inputStyle(t), flex: 1, fontFamily: 'inherit' }} />
              <button style={btnStyle(t, true)} onClick={save}>Save</button>
              <button style={btnStyle(t)} onClick={() => setEditing(null)}>Cancel</button>
            </>
          ) : (
            <>
              <div style={{ flex: 1, fontSize: 13, whiteSpace: 'pre-wrap' }}>
                {f.text}
                <div style={{ fontSize: 11, color: t.textSecondary }}>
                  updated {fmtDate(f.updated_at)}{f.conflict_id ? ` · resolves conflict #${f.conflict_id}` : ''}
                </div>
              </div>
              <button style={btnStyle(t)} onClick={() => setEditing({ id: f.id, text: f.text })}>Edit</button>
              <button style={{ ...btnStyle(t), color: '#c0392b' }} onClick={() => del(f)}>Delete</button>
            </>
          )}
        </div>
      ))}
      {rows.length === 0 && <div style={{ color: t.textSecondary, fontSize: 13 }}>No facts yet.</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// Admin: conflicts

function ConflictsTab({ t, onOpen, onChanged }) {
  const [status, setStatus] = useState('open');
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState(null);
  const [typed, setTyped] = useState({});
  const load = useCallback(() => faq.conflicts({ status }).then((r) => setRows(r.data)).catch((e) => setMsg(errText(e, 'Load failed'))), [status]);
  useEffect(() => { load(); }, [load]);
  const resolve = async (c, data) => {
    try { await faq.resolveConflict(c.id, data); setMsg(null); load(); onChanged(); } catch (e) { setMsg(errText(e, 'Resolve failed')); }
  };
  return (
    <div style={{ padding: 16, maxWidth: 1100 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['open', 'resolved'].map((s) => (
          <button key={s} style={btnStyle(t, status === s)} onClick={() => setStatus(s)}>{s === 'open' ? 'Open' : 'Resolved'}</button>
        ))}
      </div>
      {msg && <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 8 }}>{msg}</div>}
      {rows.length === 0 && <div style={{ color: t.textSecondary, fontSize: 13 }}>No {status} conflicts.</div>}
      {rows.map((c) => (
        <div key={c.id} data-testid="faq-conflict-row" style={{ border: `1px solid ${t.borderColor}`, borderRadius: 8, padding: 12, marginBottom: 12, background: t.panelBg }}>
          <div style={{ fontSize: 13 }}>
            <b>{c.company}</b> — <b>{c.topic}</b>
            <span style={{ fontSize: 11, color: t.textSecondary }}> · asked {c.question_count}× · since {fmtDate(c.created_at)}</span>
          </div>
          {c.summary && <div style={{ fontSize: 12, marginTop: 4 }}>{c.summary}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
            {c.sources.map((s, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <CitationList citations={[s]} darkMode={t.darkMode} onOpen={onOpen} />
                {c.status === 'open' && <button style={btnStyle(t)} onClick={() => resolve(c, { mode: 'side', source_index: i })}>✓ This one is right</button>}
              </div>
            ))}
          </div>
          {c.status === 'open' ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <input value={typed[c.id] || ''} onChange={(e) => setTyped({ ...typed, [c.id]: e.target.value })}
                placeholder="…or type the correct answer (becomes a fact)" style={{ ...inputStyle(t), flex: 1 }} />
              <button style={btnStyle(t, true)} disabled={!(typed[c.id] || '').trim()} onClick={() => resolve(c, { mode: 'text', text: typed[c.id] })}>Resolve</button>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: t.textSecondary, marginTop: 6 }}>Resolved {fmtDate(c.resolved_at)} by fact #{c.resolution_fact_id}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------
// Admin: question log

function LogTab({ t, companies, onOpen }) {
  const [companyId, setCompanyId] = useState('');
  const [unanswered, setUnanswered] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    const params = { page };
    if (companyId) params.company_id = companyId;
    if (unanswered) params.unanswered = true;
    faq.questions(params).then((r) => setData(r.data)).catch((e) => setMsg(errText(e, 'Load failed')));
  }, [companyId, unanswered, page]);
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
        <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setPage(1); }} style={inputStyle(t)}>
          <option value="">All companies</option>
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" data-testid="faq-unanswered" checked={unanswered} onChange={(e) => { setUnanswered(e.target.checked); setPage(1); }} /> Unanswered only
        </label>
        <span style={{ fontSize: 12, color: t.textSecondary }}>{data.total} question(s)</span>
        <span style={{ flex: 1 }} />
        <button style={btnStyle(t)} disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
        <span style={{ fontSize: 12 }}>{page} / {pages}</span>
        <button style={btnStyle(t)} disabled={page >= pages} onClick={() => setPage(page + 1)}>›</button>
      </div>
      {msg && <div style={{ color: '#c0392b', fontSize: 12 }}>{msg}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead><tr style={{ textAlign: 'left', color: t.textSecondary }}><th>When</th><th>User</th><th>Company</th><th>Question</th><th>Answered</th><th>Sources</th><th>Model</th><th>Time</th></tr></thead>
        <tbody>
          {data.items.map((r) => (
            <React.Fragment key={r.id}>
              <tr style={{ borderTop: `1px solid ${t.borderColor}`, cursor: 'pointer' }} onClick={() => setOpen(open === r.id ? null : r.id)}>
                <td style={{ padding: '5px 0', whiteSpace: 'nowrap' }}>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                <td>{r.user}</td><td>{r.company}</td>
                <td style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.question}</td>
                <td>{r.status === 'error' ? '⚠ error' : (r.answered ? '✓' : '✗')}{(r.conflicts || []).length ? ' ⚠conflict' : ''}</td>
                <td>{r.citations.length}{r.dropped ? ` (−${r.dropped})` : ''}</td>
                <td>{r.model}{r.thinking ? ' · think' : ''}</td>
                <td>{r.latency_ms != null ? `${Math.round(r.latency_ms / 100) / 10}s` : ''}</td>
              </tr>
              {open === r.id && (
                <tr><td colSpan={8} style={{ padding: '6px 10px 12px', background: t.bg }}>
                  <div style={{ fontSize: 12, fontWeight: 600 }}>Q: {r.question}</div>
                  <div style={{ fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 4 }}>{r.status === 'error' ? `Error: ${r.error}` : r.answer}</div>
                  {(r.conflicts || []).map((c) => <ConflictBox key={c.conflict_id} conflict={c} darkMode={t.darkMode} onOpen={onOpen} />)}
                  <CitationList citations={r.citations} darkMode={t.darkMode} onOpen={onOpen} />
                  <div style={{ fontSize: 10, color: t.textSecondary, marginTop: 4 }}>
                    {r.tier} · retries {r.retries}{r.docs_omitted ? ` · ${r.docs_omitted} doc(s) omitted` : ''} · {r.ask_id}
                  </div>
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------

export default function FaqApp({ user, darkMode, onClose, onConflictsChanged }) {
  const isAdmin = user?.role === 'admin';
  const t = useTheme(darkMode);
  const [tab, setTab] = useState('ask');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [viewer, setViewer] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const loadCompanies = useCallback(() => faq.companies().then((r) => { setCompanies(r.data); setLoadErr(null); })
    .catch((e) => setLoadErr(errText(e, 'Could not load companies'))), []);
  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !viewer) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, viewer]);

  const tabs = [
    { id: 'ask', label: 'Ask' },
    ...(isAdmin ? [
      { id: 'companies', label: 'Companies' },
      { id: 'documents', label: 'Documents' },
      { id: 'facts', label: 'Facts' },
      { id: 'conflicts', label: 'Conflicts' },
      { id: 'log', label: 'Question log' },
    ] : []),
  ];
  const companyScoped = tab === 'documents' || tab === 'facts';

  return (
    <div data-testid="faq-app" style={{ position: 'fixed', inset: 0, background: t.bg, color: t.textPrimary, zIndex: 900, display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: t.panelBg, borderBottom: `1px solid ${t.borderColor}`, padding: '0 18px', display: 'flex', alignItems: 'center', height: 50, gap: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: BLUE }}>❓ FAQ</span>
        {tabs.length > 1 && (
          <div style={{ display: 'flex', borderRadius: 5, overflow: 'hidden', border: `1px solid ${t.borderColor}`, marginLeft: 8 }}>
            {tabs.map((x) => (
              <button key={x.id} data-testid={`faq-tab-${x.id}`} onClick={() => setTab(x.id)}
                style={{ padding: '7px 16px', border: 'none', background: tab === x.id ? BLUE : t.panelBg, color: tab === x.id ? '#fff' : t.textPrimary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                {x.label}
              </button>
            ))}
          </div>
        )}
        {companyScoped && (
          <select value={companyId || ''} onChange={(e) => setCompanyId(Number(e.target.value) || null)} style={{ ...inputStyle(t), minWidth: 220 }}>
            <option value="">— pick a company —</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={btnStyle(t)}>Close</button>
      </div>
      {loadErr && <div style={{ padding: 12, color: '#c0392b' }}>{loadErr}</div>}
      <div style={{ flex: 1, minHeight: 0, overflowY: tab === 'ask' ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column' }}>
        {tab === 'ask' && <AskTab t={t} companies={companies} companyId={companyId} setCompanyId={setCompanyId} onOpen={setViewer} />}
        {isAdmin && tab === 'companies' && <CompaniesTab t={t} reload={loadCompanies} />}
        {isAdmin && tab === 'documents' && <DocumentsTab t={t} companyId={companyId} reload={loadCompanies} onOpen={setViewer} />}
        {isAdmin && tab === 'facts' && <FactsTab t={t} companyId={companyId} reload={loadCompanies} />}
        {isAdmin && tab === 'conflicts' && <ConflictsTab t={t} onOpen={setViewer} onChanged={() => { loadCompanies(); onConflictsChanged && onConflictsChanged(); }} />}
        {isAdmin && tab === 'log' && <LogTab t={t} companies={companies} onOpen={setViewer} />}
      </div>
      {viewer && <SourceViewer cite={viewer} t={t} onClose={() => setViewer(null)} />}
    </div>
  );
}
